import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { MODEL_CONFIG_DEFAULT_DATA } from '#pplx/frontend/ask-models/hooks/internal/consts.js';
import { useModelConfigReadinessFromQueryStateVariation } from '#pplx/frontend/ask-models/hooks/internal/useModelConfigReadinessFromQueryStateVariation.js';
import { createGetSearchModelsQuery, getSearchModelConfigQuery, getSearchModelsQueryKey, } from '#pplx/frontend/ask-models/hooks/utilities/getSearchModelConfigQuery.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { useLocalStorage } from '#pplx/frontend/browser-storage/hooks/useLocalStorage.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
const HAS_LOADED_MODEL_CONFIG_FROM_SERVER_META_KEY = 'hasLoadedModelConfigFromServer';
// Let a returning tab refresh after a day so it can learn about retired models
// without the user having to reload the page.
const MODEL_CONFIG_STALE_TIME_MS = 24 * 60 * 60 * 1000;
// Shares the user-settings prefix so existing sign-out cleanup removes this hint.
const INSTANT_HARNESS_CACHE_KEY = 'pplx.local-user-settings.instant-harness-v1';
// Read the cache by hash instead of scanning every query on each render.
const getSearchModelsQuery = (queryClient, queryKey) => queryClient.getQueryCache().get(queryClient.defaultQueryOptions({ queryKey }).queryHash);
const queryHasLoadedModelConfigFromServer = (queryClient, queryKey) => getSearchModelsQuery(queryClient, queryKey)?.meta?.[HAS_LOADED_MODEL_CONFIG_FROM_SERVER_META_KEY] === true;
/** @testonly */
export const MODEL_CONFIG_QUERY_PLACEHOLDER_DATA = createGetSearchModelsQuery(MODEL_CONFIG_DEFAULT_DATA);
export const useSearchModels = ({ reason, enabled = true, retry, }) => {
    const userId = useSession().session?.user.id;
    const [, , getCachedHarness] = useLocalStorage(INSTANT_HARNESS_CACHE_KEY, null, { broadcast: false });
    const { variation: shouldUseQueryStateReadiness } = useModelConfigReadinessFromQueryStateVariation({
        defaultValue: false,
    });
    const queryClient = useQueryClient();
    const queryKey = getSearchModelsQueryKey();
    const queryMeta = getSearchModelsQuery(queryClient, queryKey)?.meta;
    const query = useQuery({
        queryFn: async () => {
            const queryEntry = getSearchModelsQuery(queryClient, queryKey);
            const data = await getSearchModelConfigQuery({ reason });
            queryEntry?.setOptions({
                ...queryEntry.options,
                meta: {
                    ...queryEntry.meta,
                    [HAS_LOADED_MODEL_CONFIG_FROM_SERVER_META_KEY]: true,
                },
            });
            return data;
        },
        queryKey,
        staleTime: MODEL_CONFIG_STALE_TIME_MS,
        enabled: enabled,
        // Catalogs carry organization policy; a failed refresh must remain an error.
        meta: { ...queryMeta, skipPersistence: true },
        placeholderData: keepPreviousData,
        retry,
    });
    useEffect(() => {
        if (query.data === undefined)
            return;
        const harness = query.data.computer_harness;
        if (!harness?.modes.instant) {
            SafeLocalStorage.removeItem(INSTANT_HARNESS_CACHE_KEY);
        }
        else if (userId) {
            const serializedHint = JSON.stringify({ userId, harness });
            if (SafeLocalStorage.getItem(INSTANT_HARNESS_CACHE_KEY) !== serializedHint) {
                SafeLocalStorage.setItem(INSTANT_HARNESS_CACHE_KEY, serializedHint);
            }
        }
    }, [query.data, userId]);
    const data = query.data ?? MODEL_CONFIG_QUERY_PLACEHOLDER_DATA;
    const cachedHarness = query.isPending && query.data === undefined ? getCachedHarness() : null;
    // This flag temporarily gates the query-state readiness rollout.
    // Remove the meta-marker path when the flag is retired.
    const hasLoadedModelConfigFromServer = shouldUseQueryStateReadiness
        ? query.isFetched &&
            query.data !== undefined &&
            !query.isPlaceholderData &&
            query.dataUpdatedAt > 0
        : query.dataUpdatedAt > 0 &&
            !!query.data &&
            queryHasLoadedModelConfigFromServer(queryClient, queryKey);
    return {
        query,
        isLoaded: !!query.data,
        hasLoadedModelConfigFromServer,
        ...data,
        computer_harness: userId && cachedHarness?.userId === userId && cachedHarness.harness?.modes?.instant
            ? cachedHarness.harness
            : data.computer_harness,
    };
};
//# sourceMappingURL=useSearchModels.js.map